import {
  createContext,
  MutableRefObject,
  ReactNode,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';

import { SatoshiDrawer } from '~/modules/satoshi/components/SatoshiDrawer';

import { IFCWithChildren } from '~/common/interfaces/IFCWithChildren';
import { useMediaQuery } from '~/common/hooks/use-media-query';

import { SubscriptionUpgradeModal } from '../components/SubscriptionUpgradeModal';

interface IPrivateLayoutContextData {
  mobileHeaderTopRightContent: ReactNode;
  bodyRef: MutableRefObject<HTMLDivElement>;
  isMobile: boolean;
  renderMobileHeaderTopRightContent: (content: ReactNode) => void;
  handleOpenMobileSatoshiDrawer: () => void;
  callSubscriptionUpgradeModal: (to: 'platinum' | 'standard') => void;
}

const PrivateLayoutContext = createContext({} as IPrivateLayoutContextData);

interface IPrivateLayoutProviderProps {
  isMobile: boolean;
}
const PrivateLayoutProvider: IFCWithChildren<IPrivateLayoutProviderProps> = ({
  children,
  isMobile: isMobileInitialProps,
}) => {
  const isMobile = useMediaQuery('(max-width: 750px)') || isMobileInitialProps;
  const bodyRef = useRef<HTMLDivElement>(null);
  const [openSubscriptionUpgradeModal, setOpenSubscriptionUpgradeModal] =
    useState<'platinum' | 'standard'>();
  const [isSatoshiDrawerOpen, setIsSatoshiDrawerOpen] =
    useState<boolean>(false);

  const handleOpenMobileSatoshiDrawer = useCallback(() => {
    setIsSatoshiDrawerOpen(true);
  }, []);

  const [mobileHeaderTopRightContent, setMobileHeaderTopRightContent] =
    useState<ReactNode>(null);

  const callSubscriptionUpgradeModal = useCallback(
    (to: 'platinum' | 'standard') => {
      setOpenSubscriptionUpgradeModal(to);
    },
    []
  );

  const renderMobileHeaderTopRightContent = (content: ReactNode): void => {
    setMobileHeaderTopRightContent(content);
  };

  return (
    <PrivateLayoutContext.Provider
      value={{
        bodyRef,
        isMobile,
        mobileHeaderTopRightContent,
        callSubscriptionUpgradeModal,
        handleOpenMobileSatoshiDrawer,
        renderMobileHeaderTopRightContent,
      }}
    >
      <SubscriptionUpgradeModal
        to={openSubscriptionUpgradeModal}
        open={!!openSubscriptionUpgradeModal}
        close={() => setOpenSubscriptionUpgradeModal(null)}
      />

      <SatoshiDrawer
        isOpen={isSatoshiDrawerOpen}
        onClose={() => setIsSatoshiDrawerOpen(false)}
      />

      {children}
    </PrivateLayoutContext.Provider>
  );
};

const usePrivateLayout = (): IPrivateLayoutContextData => {
  return useContext(PrivateLayoutContext);
};

export { PrivateLayoutProvider, usePrivateLayout };
